import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { dashboardService } from '../services/dashboard';

const detailCache = new Map();

export function ModelNameWithDetail({ model, tier, label, compact = false, emphasis = 'routed' }) {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const anchorRef = useRef(null);
  const showTimer = useRef(null);
  const hideTimer = useRef(null);

  const cacheKey = `${model}:${tier || 'equal'}`;
  const displayLabel = label || model;

  const updatePosition = () => {
    if (!anchorRef.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    const popoverWidth = 320;
    const left = Math.min(rect.left, window.innerWidth - popoverWidth - 12);
    setPosition({ top: rect.bottom + 6, left: Math.max(12, left) });
  };

  const fetchDetail = () => {
    if (detailCache.has(cacheKey)) {
      setDetail(detailCache.get(cacheKey));
      setLoading(false);
      setError(false);
      return;
    }
    setLoading(true);
    setError(false);
    dashboardService.modelDetail(
      model,
      tier,
      (res) => {
        detailCache.set(cacheKey, res.data);
        setDetail(res.data);
        setLoading(false);
      },
      () => {
        setLoading(false);
        setError(true);
      },
    );
  };

  const handleEnter = () => {
    clearTimeout(hideTimer.current);
    showTimer.current = setTimeout(() => {
      updatePosition();
      setOpen(true);
      fetchDetail();
    }, 200);
  };

  const handleLeave = () => {
    clearTimeout(showTimer.current);
    hideTimer.current = setTimeout(() => setOpen(false), 150);
  };

  const keepOpen = () => {
    clearTimeout(hideTimer.current);
  };

  useEffect(() => {
    if (!open) return undefined;
    const onScroll = () => setOpen(false);
    window.addEventListener('scroll', onScroll, true);
    return () => window.removeEventListener('scroll', onScroll, true);
  }, [open]);

  useEffect(
    () => () => {
      clearTimeout(showTimer.current);
      clearTimeout(hideTimer.current);
    },
    [],
  );

  const triggerClass = compact
    ? 'inline-flex items-center gap-1 font-mono text-xs text-theme-primary/80 underline decoration-dotted decoration-theme-primary/60 underline-offset-2 cursor-help hover:text-theme-primary hover:decoration-solid'
    : 'inline-flex items-center gap-1 font-mono text-sm text-theme-primary/90 underline decoration-dotted decoration-theme-primary/60 underline-offset-2 cursor-help hover:text-theme-primary hover:decoration-solid max-w-full min-w-0';

  return (
    <>
      <span
        ref={anchorRef}
        role="button"
        tabIndex={0}
        className={triggerClass}
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
        onFocus={handleEnter}
        onBlur={handleLeave}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <span className="truncate">{displayLabel}</span>
      </span>
      {open &&
        createPortal(
          <div
            className="fixed z-[9999] w-80 rounded-xl bg-theme-main border border-theme-secondary p-3 shadow-xl text-xs"
            style={{ top: position.top, left: position.left }}
            onMouseEnter={keepOpen}
            onMouseLeave={handleLeave}
          >
            {loading && <p className="opacity-60 py-2">Loading model details…</p>}
            {error && <p className="text-red-500 py-2">Could not load model details.</p>}
            {!loading && !error && detail && (
              <ModelDetailPopover detail={detail} emphasis={emphasis} />
            )}
          </div>,
          document.body,
        )}
    </>
  );
}

export function RoutedModelDetail({ model, tier, mapping, compact = false }) {
  if (!mapping?.os_model) return null;
  return (
    <ModelNameWithDetail
      compact={compact}
      emphasis="routed"
      model={model}
      tier={tier}
      label={
        mapping.upstream_provider
          ? `${mapping.upstream_provider} · ${mapping.os_model}`
          : mapping.os_model
      }
    />
  );
}

function ModelDetailPopover({ detail, emphasis = 'routed' }) {
  if (emphasis === 'original') {
    return <OriginalModelDetailPopover detail={detail} />;
  }
  return <RoutedModelDetailPopover detail={detail} />;
}

function OriginalModelDetailPopover({ detail }) {
  const { original, routed, original_capabilities: capabilities } = detail;
  const caps = capabilities || detail.capabilities;

  return (
    <div className="space-y-3">
      <div className="flex items-baseline gap-2 flex-wrap">
        <span className="font-mono text-sm text-theme-text">{detail.input_model}</span>
        <span className="opacity-50 capitalize text-[11px]">{original?.provider}</span>
      </div>

      {detail.considerations && (
        <p className="opacity-60">{detail.considerations}</p>
      )}

      {original && (
        <div className="grid grid-cols-3 gap-2">
          <DetailStat label="Quality" value={original.quality} />
          <DetailStat label="Speed" value={`${original.tps} tps`} />
          <DetailStat label="Cost" value={`$${original.blended_price}/M`} />
        </div>
      )}

      {routed && (
        <div>
          <div className="text-[10px] uppercase tracking-wide opacity-50 mb-1">Replaced by</div>
          <div className="font-mono opacity-90">{routed.os_model}</div>
          <div className="opacity-60 mt-0.5">{routed.blurb}</div>
        </div>
      )}

      {detail.helper_text && routed && (
        <p className="opacity-80">{detail.helper_text}</p>
      )}

      {!routed && detail.helper_text && (
        <p className="opacity-70">{detail.helper_text}</p>
      )}

      {caps && <CapabilitiesBlock capabilities={caps} modalities={[]} />}
    </div>
  );
}

function RoutedModelDetailPopover({ detail }) {
  const { original, routed, differences, capabilities } = detail;

  if (!routed) {
    return (
      <div className="space-y-2">
        <div className="font-mono text-sm text-theme-text">{detail.input_model}</div>
        <p className="opacity-70">{detail.helper_text}</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div>
        <div className="font-mono text-sm text-theme-text">{routed.os_model}</div>
        <div className="opacity-60 mt-1">{routed.blurb}</div>
      </div>

      {original && (
        <div>
          <div className="text-[10px] uppercase tracking-wide opacity-50 mb-1">Replaces</div>
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="font-mono opacity-90">{detail.input_model}</span>
            <span className="opacity-50 capitalize text-[11px]">{original?.provider}</span>
          </div>
        </div>
      )}

      {detail.helper_text && (
        <p className="opacity-80">{detail.helper_text}</p>
      )}

      {differences ? (
        <div className="grid grid-cols-3 gap-2 border-t border-theme-secondary/50 pt-3">
          <DetailStat
            label="Cost"
            value={
              differences.savings_pct != null
                ? `${Math.round(differences.savings_pct)}% cheaper`
                : '—'
            }
          />
          <DetailStat label="Quality" value={differences.quality_phrase || '—'} />
          <DetailStat label="Speed" value={differences.speed_phrase || '—'} />
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-2 border-t border-theme-secondary/50 pt-3">
          <DetailStat label="Quality" value={routed.quality} />
          <DetailStat label="Speed" value={`${routed.tps} tps`} />
          <DetailStat label="Cost" value={`$${routed.blended_price}/M`} />
        </div>
      )}

      {routed.providers?.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wide opacity-50 mb-1">Providers</div>
          <div className="flex flex-wrap gap-1.5">
            {routed.providers.map((p) => (
              <span
                key={p.provider}
                className={`px-2 py-0.5 rounded-full border text-[11px] ${
                  p.configured
                    ? 'border-theme-secondary opacity-90'
                    : 'border-dashed border-theme-secondary opacity-40'
                }`}
              >
                {p.provider_name}
                {p.configured ? ` · ${p.tps} tps` : ''}
              </span>
            ))}
          </div>
        </div>
      )}

      {capabilities && (
        <CapabilitiesBlock capabilities={capabilities} modalities={routed.modalities} />
      )}
    </div>
  );
}

function CapabilitiesBlock({ capabilities, modalities = [] }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide opacity-50 mb-1">Capabilities</div>
      <div className="flex flex-wrap gap-1.5 mb-1.5">
        {capabilities.tools && (
          <span className="px-2 py-0.5 rounded-full bg-theme-secondary/60 text-[11px]">Tools</span>
        )}
        {capabilities.reasoning_effort && (
          <span className="px-2 py-0.5 rounded-full bg-theme-secondary/60 text-[11px]">Reasoning</span>
        )}
        {capabilities.reasoning_stream && (
          <span className="px-2 py-0.5 rounded-full bg-theme-secondary/60 text-[11px]">Reasoning stream</span>
        )}
        {capabilities.streaming !== false && (
          <span className="px-2 py-0.5 rounded-full bg-theme-secondary/60 text-[11px]">Streaming</span>
        )}
        {capabilities.response_format && (
          <span className="px-2 py-0.5 rounded-full bg-theme-secondary/60 text-[11px]">JSON schema</span>
        )}
        {modalities.map((m) => (
          <span key={m} className="px-2 py-0.5 rounded-full bg-theme-secondary/60 text-[11px] capitalize">
            {m}
          </span>
        ))}
      </div>
      {capabilities.supported_params?.length > 0 && (
        <div className="opacity-60 font-mono text-[10px] leading-relaxed">
          Params: {capabilities.supported_params.join(', ')}
        </div>
      )}
    </div>
  );
}

function DetailStat({ label, value }) {
  return (
    <div>
      <div className="opacity-50 text-[10px] uppercase tracking-wide">{label}</div>
      <div className="font-medium mt-0.5">{value}</div>
    </div>
  );
}
